/**
 * Lean react-dom/client — moon, not universe.
 *
 * Used when `jetshake({ leanBoot: true })` (or JETSHAKE_LEAN_BOOT=1). Fully
 * compiled apps boot through the signal/DOM path only: no compat-init, no
 * reconcile-root, no component.js reconciler cluster, no render-component.
 *
 * Default builds keep src/compat/react-dom-client.ts (byte-identical).
 *
 * Hydration (piece 158): when the bundle was built with `jetshake({ ssr:
 * true })`, compiled output creates DOM through the $h claim helpers, so
 * hydrateRoot adopts the prerendered nodes in place (no clear, no rebuild).
 * Bundles built WITHOUT the ssr flag claim nothing - endCompiledHydration
 * then removes the server nodes as leftovers, which is exactly the old
 * clear-and-mount behavior. A structural mismatch mid-claim falls back to a
 * full clear + fresh client render (React's mismatch recovery).
 */
import { batch } from '../runtime/compiled/signal-core.js';
import { startCompiledHydration, endCompiledHydration, didCompiledHydrationBail, } from '../runtime/compiled/hydrate.js';
import { getNetMount } from '../shared/lazy-compat.js';
function mountElement(container, element) {
    if (element == null)
        return;
    const type = element.type != null ? element.type : element;
    const props = element.props != null ? element.props : {};
    let dom;
    if (typeof type === 'function') {
        // Compiled roots are plain `Component(props) -> Node` calls. Do not import
        // render-component here — a static import would pin ~64KB into every
        // leanBoot app even when the fallback path is never taken.
        if (!type._isCompiled) {
            // Lazy compat safety net (EPIC-MOON-002 R4: registration-gated). The
            // branch body lives in compat/lazy-net-mount.ts and is registered by
            // the same injected virtual module that registers the compat loader —
            // a build whose net can never fire ships neither the branch nor seam.
            const net = getNetMount();
            if (net)
                return net(container, type, props);
            throw new Error('leanBoot: root must be a JetShake-compiled component (missing _isCompiled).');
        }
        dom = type(props);
    }
    else if (typeof type === 'string') {
        throw new Error('leanBoot: host-element roots are not supported; mount a compiled component.');
    }
    if (dom instanceof Node && dom.parentNode !== container) {
        // Hydration-claimed roots are already in position; a same-parent
        // appendChild would move them past unclaimed siblings.
        container.appendChild(dom);
    }
    return dom;
}
class LeanRoot {
    _container;
    _unmounted = false;
    _element = null;
    constructor(container) {
        this._container = container;
    }
    render(element) {
        if (this._unmounted) {
            throw new Error('Cannot update an unmounted root.');
        }
        this._element = element;
        batch(() => {
            if (!this._container._jetMounted) {
                const tag = this._container.tagName && this._container.tagName.toLowerCase();
                if (tag !== 'html' && tag !== 'body' && tag !== 'head') {
                    this._container.textContent = '';
                }
            }
            mountElement(this._container, element);
            this._container._jetMounted = true;
        });
    }
    unmount() {
        this._unmounted = true;
        this._container.textContent = '';
        this._container._jetMounted = false;
    }
}
export function createRoot(container, _options) {
    return new LeanRoot(container);
}
/**
 * Lean hydrateRoot - compiled claim-or-create (piece 158).
 *
 * Prerendered container: run the mount with the hydration cursor active so
 * $h-built bundles claim the server nodes in place. Leftover removal inside
 * endCompiledHydration makes a non-ssr bundle degrade to the old
 * clear-and-mount (fresh DOM in, server DOM out); a mid-claim bail clears
 * and re-renders fresh so a mismatch can never leave doubled content.
 */
export function hydrateRoot(container, initialChildren, _options) {
    const root = new LeanRoot(container);
    if (!container.hasChildNodes || !container.hasChildNodes()) {
        root.render(initialChildren);
        return root;
    }
    startCompiledHydration(container);
    try {
        batch(() => {
            mountElement(container, initialChildren);
            container._jetMounted = true;
        });
        root._element = initialChildren;
    }
    finally {
        endCompiledHydration();
    }
    if (didCompiledHydrationBail()) {
        const tag = container.tagName && container.tagName.toLowerCase();
        if (tag !== 'html' && tag !== 'body' && tag !== 'head') {
            container.textContent = '';
        }
        container._jetMounted = false;
        root.render(initialChildren);
    }
    return root;
}
export default { createRoot, hydrateRoot };
